import { LitElement, html, render } from "lit";
import { property } from "lit/decorators/property.js";
import { postSocialShareAnalyticsEvent } from "../../api-sdk/v2/users/events";
import { DEFAULT_BASE_URL } from "../../api-sdk/constants";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import { LANGUAGES } from "../../api-sdk/types";
import { urlToFile } from "../files";
import { isMobile } from "../feature-detection";
import { strings } from "./strings/share-button";

interface SlDialogElement extends HTMLElement {
  show: () => void;
  hide: () => void;
}

export class BeamShareButton extends LitElement {
  @property({ type: String }) shareIcon: string | null = null;
  @property({ type: String }) imageUrl?: string | null = null;
  @property({ type: String }) brandName: string | null = null;
  @property({ type: String }) buttonText?: string | null = null;
  @property({ type: String }) configLang: LANGUAGES = "en";
  @property({ type: String }) baseUrl: string = DEFAULT_BASE_URL;
  @property({ type: String }) nonprofitId?: number | null = null;
  @property({ type: String }) storeId?: number | null = null;
  @property({ type: String }) transactionId?: number | null = null;
  @property({ type: String }) apiKey!: string;

  connectedCallback() {
    super.connectedCallback();
  }

  async handleShareClick() {
    try {
      if (!this.imageUrl || !this.brandName || !this.configLang) return;
      await import("./_share-dialog-dependencies.js");

      const { nonprofitId, storeId, transactionId, baseUrl, apiKey } = this;
      void postSocialShareAnalyticsEvent({
        baseUrl,
        apiKey,
        requestBody: { eventName: "shareImageButtonClick", nonprofitId, storeId, timestamp: Date.now(), transactionId },
      }).catch(() => null);

      if (isMobile()) {
        this.openSystemShareSheet(this.imageUrl);
      } else {
        this.handleShareDialog(this.imageUrl, this.brandName);
      }
    } catch (err) {
      console.error("Error sharing impact:", err);
    }
  }

  async handleShareDialog(imageUrl: string, brandName: string) {
    if (!imageUrl) return;

    const existingDialog = this.shadowRoot?.querySelector("sl-dialog") as SlDialogElement;

    if (existingDialog) {
      existingDialog.show();
      return;
    }

    await import("./_share-dialog-dependencies.js"); // Import libraries async to reduce bundle size if share feature is unused

    const modalRoot = this.shadowRoot?.querySelector("#modal-root") as HTMLElement;

    const file = await urlToFile(imageUrl);

    const dataUrl = URL.createObjectURL(file);

    const handleClickClose = () => {
      (this.shadowRoot?.querySelector("sl-dialog") as SlDialogElement)?.hide();
    };

    const handleClickCopy = async () => {
      void postSocialShareAnalyticsEvent({
        baseUrl: this.baseUrl,
        apiKey: this.apiKey,
        requestBody: {
          eventName: "copyImageButtonClick",
          nonprofitId: this.nonprofitId,
          storeId: this.storeId,
          timestamp: Date.now(),
          transactionId: this.transactionId,
        },
      }).catch(() => null);
      await navigator.clipboard.write([new ClipboardItem({ "image/png": file })]);
      setTimeout(handleClickClose, 250);
    };

    const handleClickDownload = () => {
      void postSocialShareAnalyticsEvent({
        baseUrl: this.baseUrl,
        apiKey: this.apiKey,
        requestBody: {
          eventName: "downloadImageButtonClick",
          nonprofitId: this.nonprofitId,
          storeId: this.storeId,
          timestamp: Date.now(),
          transactionId: this.transactionId,
        },
      }).catch(() => null);
      setTimeout(handleClickClose, 250);
    };

    const dialogTemplate = html`
      <style>
        .plain-button {
          border: none;
          background: none;
          font-size: inherit;
          font-family: inherit;
        }
        .share-dialog::part(panel) {
          padding: var(--beam-share-dialog-padding);
          text-align: center;
        }
        .share-title {
          font-size: var(--beam-share-title-fontSize);
          font-weight: var(--beam-share-title-fontWeight);
        }
        .share-text {
          font-size: var(--beam-share-text-fontSize);
          line-height: var(--beam-share-text-lineHeight);
          margin-top: var(--beam-share-text-marginTop);
        }
        .share-image {
          border-radius: var(--beam-share-image-borderRadius);
          width: var(--beam-share-image-width);
          display: block;
          margin: var(--beam-share-image-margin) auto;
        }
        .share-actions {
          margin: var(--beam-share-actions-margin);
        }
        .share-action-button {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          padding: 5px;
        }
        .share-action-icon {
          font-size: var(--beam-share-action-icon-size);
        }
        .share-action-button:hover .share-action-icon {
          scale: 1.1;
        }
        .share-action-text {
          font-size: var(--beam-share-action-text-fontSize);
          line-height: var(--beam-share-action-text-lineHeight);
          margin-top: var(--beam-share-text-action-marginTop);
        }
        .plain-button {
          border: none;
          background: none;
          font-size: inherit;
          font-family: inherit;
        }
        .plain-link {
          text-decoration: none;
          color: inherit;
        }
        .share-close {
          display: block;
          background-color: var(--beam-share-close-backgroundColor);
          color: var(--beam-share-close-text-color);
          justify-content: center;
          padding: var(--beam-share-close-padding);
          border-radius: var(--beam-share-close-borderRadius);
          width: 100%;
          font-size: var(--beam-share-close-fontSize);
          font-weight: var(--beam-share-close-fontWeight);
        }
        .share-close:hover {
          outline: 2px solid var(--beam-share-close-focus-borderColor);
        }
      </style>
      <sl-dialog open label="${strings[this.configLang].impactShareTitle()}" no-header class="share-dialog">
        <h2 class="share-title">${strings[this.configLang].impactShareTitle()}</h2>
        <p class="share-text">${strings[this.configLang].impactShareText({ brandName })}</p>
        <img src="${imageUrl}" alt="${strings[this.configLang].impactShareTitle()}" class="share-image" />
        <div class="share-actions" style="display: flex; justify-content: space-evenly">
          <div style="flex: 1; text-align: center">
            <button class="plain-button button share-action-button" @click="${handleClickCopy}">
              <sl-icon
                library="lucide"
                name="copy"
                label="${strings[this.configLang].impactShareCopy()}"
                class="share-action-icon"
              ></sl-icon>
              <div class="share-action-text">${strings[this.configLang].impactShareCopy()}</div>
            </button>
          </div>
          <div style="flex: 1; text-align: center">
            <a
              class="plain-button button share-action-button plain-link"
              download="${strings[this.configLang].impactShareTitle()}"
              href="${dataUrl}"
              @click="${handleClickDownload}"
            >
              <sl-icon
                library="lucide"
                name="download"
                label="${strings[this.configLang].impactShareDownload()}"
                class="share-action-icon"
              ></sl-icon>
              <div class="share-action-text">${strings[this.configLang].impactShareDownload()}</div>
            </a>
          </div>
        </div>
        <button class="plain-button button share-close" @click="${handleClickClose}">
          ${strings[this.configLang].impactShareClose()}
        </button>
      </sl-dialog>
    `;
    if (modalRoot) {
      render(dialogTemplate, modalRoot);
    }
  }

  async openSystemShareSheet(imageUrl: string) {
    if (!imageUrl) return;

    const file = await urlToFile(imageUrl);

    if (navigator.share) {
      await navigator
        .share({
          files: [file],
        })
        .then(() => {
          const { nonprofitId, storeId, transactionId, baseUrl, apiKey } = this;
          void postSocialShareAnalyticsEvent({
            baseUrl,
            apiKey,
            requestBody: { eventName: "shareSheetShare", nonprofitId, storeId, timestamp: Date.now(), transactionId },
          }).catch(() => null);
        })
        .catch(() => null);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }
  render() {
    if (!this.imageUrl || !this.brandName || !this.configLang) return html``;
    return html`
      <style>
        .share-button {
          font-family: var(--beam-share-button-fontFamily);
          font-size: var(--beam-share-button-fontSize);
          font-weight: var(--beam-share-button-fontWeight);
          margin-top: var(--beam-share-button-marginTop);
          margin-bottom: var(--beam-share-button-marginBottom);
          text-transform: var(--beam-share-button-textTransform);
          line-height: var(--beam-share-button-lineHeight);
          background-color: var(--beam-share-button-backgroundColor);
          color: var(--beam-share-button-color, inherit);
          border-radius: var(--beam-share-button-borderRadius);
          border-color: var(--beam-share-button-borderColor);
          border-width: var(--beam-share-button-borderWidth);
          /* add extra padding on side without icon for optical alignment */
          padding: var(--beam-share-button-padding);
          width: var(--beam-share-button-width);
          transition: 0.15s;
        }
        .share-button:hover {
          background-color: var(--beam-share-button-hover-backgroundColor);
          color: var(--beam-share-button-hover-color);
          border-color: var(--beam-share-button-hover-borderColor);
        }
        .share-button-text {
          text-decoration: var(--beam-share-button-text-textDecoration);
          margin-left: 0.33em;
        }
        .share-button-icon {
          font-size: var(--beam-share-button-icon-size);
        }
        .button {
          cursor: pointer;
        }
        .icon-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
      </style>
      <button
        @click="${this.handleShareClick}"
        baseUrl="${this.baseUrl}"
        class="share-button share-button-inline button icon-button"
      >
        <sl-icon
          name="${this.shareIcon}"
          library="lucide"
          label="${strings[this.configLang].impactShareButton()}"
          class="share-button-icon"
        ></sl-icon>
        <span class="share-button-text">${this.buttonText || strings[this.configLang].impactShareButton()}</span>
      </button>
      <div id="modal-root"></div>
    `;
  }
}

customElements.get("beam-share-button") || customElements.define("beam-share-button", BeamShareButton);

export const shareButtonConfigDefaults = {
  "--beam-share-button-fontFamily": "inherit",
  "--beam-share-button-fontSize": "14px",
  "--beam-share-button-fontWeight": "600",
  "--beam-share-button-textTransform": "none",
  "--beam-share-button-marginTop": "10px",
  "--beam-share-button-marginBottom": "10px",
  "--beam-share-button-padding": "8px 18px 8px 16px",
  "--beam-share-button-lineHeight": "inherit",
  "--beam-share-button-color": "#0A323C",
  "--beam-share-button-backgroundColor": "#fff",
  "--beam-share-button-borderRadius": "30px",
  "--beam-share-button-borderColor": "#0A323C",
  "--beam-share-button-borderWidth": "1px",

  "--beam-share-button-width": "auto",

  "--beam-share-button-hover-borderColor": "transparent",
  "--beam-share-button-hover-backgroundColor": "#0A323C",
  "--beam-share-button-hover-color": "#fff",

  "--beam-share-button-icon-size": "16px",

  "--beam-share-button-text-textDecoration": "none",

  "--beam-share-dialog-padding": "30px 30px",

  "--beam-share-title-fontSize": "30px",
  "--beam-share-title-fontWeight": "bold",

  "--beam-share-text-fontSize": "16px",
  "--beam-share-text-lineHeight": "1.3",
  "--beam-share-text-marginTop": "10px",

  "--beam-share-image-borderRadius": "4px",
  "--beam-share-image-width": "200px",
  "--beam-share-image-margin": "20px",

  "--beam-share-actions-margin": "30px 60px",
  "--beam-share-action-icon-size": "32px",

  "--beam-share-action-text-fontSize": "16px",
  "--beam-share-action-text-lineHeight": "1.3",
  "--beam-share-text-action-marginTop": "10px",

  "--beam-share-close-backgroundColor": "#ffd522",
  "--beam-share-close-text-color": "#000",
  "--beam-share-close-padding": "12px",
  "--beam-share-close-borderRadius": "4px",
  "--beam-share-close-fontSize": "16px",
  "--beam-share-close-fontWeight": "bold",

  "--beam-share-close-focus-borderColor": "#c2dcff",
} as const;

declare global {
  interface HTMLElementTagNameMap {
    "beam-share-button": BeamShareButton;
  }
}
